import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { RefreshCw } from "lucide-react";
import { AuthProvider, useAuth, isRespAdmin } from "@/context/AuthContext";
import api from "@/lib/api";
import { applyUpdate } from "@/registerServiceWorker";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import IscrizionePubblica from "@/pages/IscrizionePubblica";
import RegistrazioneStaff from "@/pages/RegistrazioneStaff";
import Dashboard from "@/pages/Dashboard";
import Segreteria from "@/pages/Segreteria";
import Animati from "@/pages/Animati";
import Animatori from "@/pages/Animatori";
import Responsabili from "@/pages/Responsabili";
import AccountGenitori from "@/pages/AccountGenitori";
import Squadre from "@/pages/Squadre";
import Postazioni from "@/pages/Postazioni";
import MiaPostazione from "@/pages/MiaPostazione";
import Giochi from "@/pages/Giochi";
import Classifica from "@/pages/Classifica";
import Calendario from "@/pages/Calendario";
import Presenze from "@/pages/Presenze";
import Camposcuola from "@/pages/Camposcuola";
import CamposcuolaStanze from "@/pages/CamposcuolaStanze";
import GestionePunti from "@/pages/GestionePunti";
import Impostazioni from "@/pages/Impostazioni";
import Registro from "@/pages/Registro";
import Bacheca from "@/pages/Bacheca";
import Profilo from "@/pages/Profilo";
import Gruppi from "@/pages/Gruppi";
import Programma from "@/pages/Programma";
import Privacy from "@/pages/Privacy";
import Diritti from "@/pages/Diritti";
import InformativaPrivacy from "@/pages/InformativaPrivacy";
import HomeAnimatore from "@/pages/HomeAnimatore";
import GenitoreRegistrazione from "@/pages/GenitoreRegistrazione";
import GenitoreHome from "@/pages/GenitoreHome";
import GenitoreFigli from "@/pages/GenitoreFigli";
import GenitoreAttivita from "@/pages/GenitoreAttivita";
import GenitoreCalendario from "@/pages/GenitoreCalendario";
import GenitoreProfilo from "@/pages/GenitoreProfilo";

function homeForUser(user) {
  if (user?.role === "genitore") return "/genitore";
  return isRespAdmin(user) ? "/dashboard" : "/inizio";
}

function Protected({ roles, children, layout = true, staffOperational = false }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-dvh flex items-center justify-center text-slate-400 font-semibold">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Le rotte senza `roles` sono storicamente quelle condivise dallo staff.
  // Un account Genitore ha una superficie separata e non deve poterla
  // raggiungere digitando direttamente l'URL.
  if (!roles && user.role === "genitore") return <Navigate to="/genitore" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeForUser(user)} replace />;
  if (staffOperational && !isRespAdmin(user)) {
    return <StaffOperationalGate layout={layout}>{children}</StaffOperationalGate>;
  }
  return layout ? <Layout>{children}</Layout> : children;
}

function StaffOperationalGate({ children, layout }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get("/operativita/stato").then((r) => {
      if (vivo) setStatus(r.data);
    }).catch(() => {
      if (vivo) setStatus({ attiva: false });
    });
    return () => { vivo = false; };
  }, []);

  if (!status) {
    return <div className="min-h-dvh flex items-center justify-center text-slate-400 font-semibold">Verifica operatività...</div>;
  }

  if (status.attiva) return layout ? <Layout>{children}</Layout> : children;

  const unavailable = (
    <div className="max-w-xl mx-auto py-12 px-4 text-center">
      <div className="card-app p-8">
        <p className="text-4xl mb-3" aria-hidden="true">🌙</p>
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Oratorio non attivo</h1>
        <p className="text-sm text-slate-500 font-semibold mt-2">
          Le sezioni operative saranno disponibili durante il periodo dell'oratorio e nei giorni configurati.
        </p>
      </div>
    </div>
  );

  return layout ? <Layout>{unavailable}</Layout> : unavailable;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-dvh flex items-center justify-center text-slate-400 font-semibold">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeForUser(user)} replace />;
}

const RA = ["admin", "responsabile"];

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/iscrizione" element={<IscrizionePubblica />} />
          <Route path="/genitore/registrazione" element={<GenitoreRegistrazione />} />
          <Route path="/genitore/figli/aggiungi" element={<GenitoreRegistrazione />} />
          <Route path="/genitore/iscrizione" element={<GenitoreRegistrazione />} />
          <Route path="/registrazione" element={<RegistrazioneStaff />} />
          <Route path="/genitore" element={<Protected roles={["genitore"]} layout={false}><GenitoreHome /></Protected>} />
          <Route path="/genitore/figli" element={<Protected roles={["genitore"]} layout={false}><GenitoreFigli /></Protected>} />
          <Route path="/genitore/attivita" element={<Protected roles={["genitore"]} layout={false}><GenitoreAttivita /></Protected>} />
          <Route path="/genitore/calendario" element={<Protected roles={["genitore"]} layout={false}><GenitoreCalendario /></Protected>} />
          <Route path="/genitore/profilo" element={<Protected roles={["genitore"]} layout={false}><GenitoreProfilo /></Protected>} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/dashboard" element={<Protected roles={RA}><Dashboard /></Protected>} />
          <Route path="/segreteria" element={<Protected roles={RA}><Segreteria /></Protected>} />
          <Route path="/animati" element={<Protected roles={RA}><Animati /></Protected>} />
          <Route path="/animatori" element={<Protected roles={RA}><Animatori /></Protected>} />
          <Route path="/responsabili" element={<Protected roles={["admin"]}><Responsabili /></Protected>} />
          <Route path="/account-genitori" element={<Protected roles={["admin"]}><AccountGenitori /></Protected>} />
          <Route path="/programma" element={<Protected staffOperational><Programma /></Protected>} />
          <Route path="/gruppi" element={<Protected><Gruppi /></Protected>} />
          <Route path="/squadre" element={<Protected roles={RA}><Squadre /></Protected>} />
          <Route path="/postazioni" element={<Protected roles={RA}><Postazioni /></Protected>} />
          <Route path="/inizio" element={<Protected roles={["animatore", "aiuto_animatore"]}><HomeAnimatore /></Protected>} />
          <Route path="/mia-postazione" element={<Protected staffOperational><MiaPostazione /></Protected>} />
          <Route path="/bacheca" element={<Protected><Bacheca /></Protected>} />
          <Route path="/giochi" element={<Protected><Giochi /></Protected>} />
          <Route path="/classifica" element={<Protected><Classifica /></Protected>} />
          <Route path="/calendario" element={<Protected><Calendario /></Protected>} />
          <Route path="/presenze" element={<Protected staffOperational><Presenze /></Protected>} />
          <Route path="/camposcuola" element={<Protected roles={RA}><Camposcuola /></Protected>} />
          <Route path="/camposcuola/stanze" element={<Protected roles={RA}><CamposcuolaStanze /></Protected>} />
          <Route path="/gestione-punti" element={<Protected roles={RA}><GestionePunti /></Protected>} />
          <Route path="/impostazioni" element={<Protected roles={RA}><Impostazioni /></Protected>} />
          <Route path="/registro" element={<Protected roles={["admin"]}><Registro /></Protected>} />
          <Route path="/diritti" element={<Protected roles={RA}><Diritti /></Protected>} />
          <Route path="/informativa-privacy" element={<Protected roles={["admin"]}><InformativaPrivacy /></Protected>} />
          <Route path="/profilo" element={<Protected><Profilo /></Protected>} />
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
        <UpdateBanner />
      </AuthProvider>
    </BrowserRouter>
  );
}

/**
 * Banner "nuova versione disponibile". Compare quando la registrazione del
 * service worker segnala un aggiornamento pronto (evento window
 * 'sw-update-available'). Stile coerente con la fascia scura dell'app.
 * Sta sopra la bottom nav su mobile grazie all'offset inferiore.
 */
function UpdateBanner() {
  const [visibile, setVisibile] = useState(false);

  useEffect(() => {
    const handler = () => setVisibile(true);
    window.addEventListener("sw-update-available", handler);
    return () => window.removeEventListener("sw-update-available", handler);
  }, []);

  if (!visibile) return null;

  return (
    <div
      className="fixed z-[60] inset-x-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] md:bottom-6 mx-auto max-w-md rounded-3xl bg-slate-900 text-white shadow-[0_8px_32px_rgba(15,23,42,0.28)] flex items-center gap-3 px-4 py-3"
      data-testid="update-banner"
      role="alert"
    >
      <RefreshCw className="h-5 w-5 text-amber-400 shrink-0" />
      <p className="text-sm font-bold flex-1 min-w-0">È disponibile una nuova versione</p>
      <button
        type="button"
        onClick={() => { setVisibile(false); applyUpdate(); }}
        className="shrink-0 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold px-4 py-1.5 text-xs transition-colors"
        data-testid="update-banner-btn"
      >
        Aggiorna
      </button>
    </div>
  );
}
